import { useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Table } from 'react-bootstrap';
import { isToday, isTomorrow, isBefore, startOfDay, parseISO } from "date-fns";
import { IntransitBasePage } from './IntransitBasePage';
import { formatCurrency, formatDate, currencySortingFn } from '../utils/formatters';
import { CopyTeamsButton } from '../components/CopyTeamsButton';

const INTERNAL_INFO_ROWS = 3;

function PickingInternalInfo({ pickings }) {
  if (!pickings || pickings.length === 0) return null;

  const freshestPicking = [...pickings].sort((a, b) => {
    const dateA = a.write_date ? parseISO(a.write_date) : new Date(0);
    const dateB = b.write_date ? parseISO(b.write_date) : new Date(0);
    return dateB - dateA;
  })[0];

  const info = freshestPicking?.internal_info;
  if (!info || info === 'False') return null;

  const lines = info.split('\n').filter(line => line.trim() !== '').slice(0, INTERNAL_INFO_ROWS);

  return (
    <div className="mt-1" style={{ fontSize: '0.75rem', lineHeight: '1.2' }}>
      {lines.map((line, i) => (
        <div key={i} className="text-muted" title={line}>
          {line}
        </div>
      ))}
    </div>
  );
}

export function IntransitOrdersPage() {
  const { mode } = useParams();
  const label = mode === 'purchases' ? 'Purchases' : 'Sales';

  const columns = useMemo(
    () => [
      {
        accessorKey: 'user_id',
        header: mode === 'purchases' ? 'Purchaser' : 'Seller',
      },
      {
        accessorKey: 'name',
        header: mode === 'purchases' ? 'PO' : 'SO',
        cell: (info) => (
            <>
                <a href={info.row.original.url} target="_blank" rel="noopener noreferrer">
                    {info.getValue()}
                </a>
                {info.row.original.pickings[0]?.to_so && <div className="text-muted small">Sold to <span className="fw-bold">{info.row.original.pickings[0]?.to_so}</span></div>}
                {info.row.original.pickings[0]?.from_po && <div className="text-muted small">From <span className="fw-bold">{info.row.original.pickings[0]?.from_po}</span></div>}
            </>
        ),
      },
      {
        accessorKey: 'partner_id',
        header: 'Company',
        cell: (info) => (
          <span>
            <div>{info.getValue()}</div>
            <PickingInternalInfo pickings={info.row.original.pickings} />
          </span>
        ),
      },
      {
        accessorKey: 'sku_quantity',
        header: 'Number of SKUS',
        cell: (info) => (
          <CopyTeamsButton
            teamsHeader={info.row.original.teams_header}
            triggerNotification={(msg) => info.table.options.meta?.triggerNotification(msg)}
          >
            {info.getValue() || '-'}
          </CopyTeamsButton>
        ),
      },
      {
        header: 'Warehouse',
        accessorFn: row => row.name.startsWith('PO') ? row.pickings[0]?.location_dest_id?.name || '' : row.pickings[0]?.location_id?.name || '',
      },
      {
        id: 'incoterm_location',
        header: 'Incoterm',
        accessorKey: 'incoterm_short',
      },
      {
        accessorKey: 'tracking_number',
        header: 'Tracking',
        cell: (info) => (
          info.row.original.tracking_url ? (
            <a href={info.row.original.tracking_url} target="_blank" rel="noopener noreferrer">
              {info.getValue() || 'Tracking'}
            </a>
          ) : (
            info.getValue() || '-'
          )
        ),
      },
      {
        accessorKey: 'amount_total',
        header: 'Amount total',
        sortingFn: currencySortingFn,
        cell: (info) => {
          const val = info.getValue();
          const amount = (typeof val === 'object' && val !== null) ? (val.amount ?? 0) : (val ?? 0);
          const dataForFmt = (typeof val === 'object' && val !== null)
            ? val
            : { amount: amount, currency: info.row.original?.currency_id };
          return (
            <span style={{ color: amount > 1000000 ? 'red' : 'inherit' }}>
              {formatCurrency(dataForFmt)}
            </span>
          );
        },
      },
      {
        id: 'amount_due',
        header: 'Amount due',
        accessorFn: (row) => {
          const invoices = row.open_invoices || [];
          const totalResidual = invoices.reduce((sum, inv) => sum + (inv.amount_residual?.amount || 0), 0);
          const currency = invoices[0]?.amount_residual?.currency || row.currency_id;
          return { amount: totalResidual, currency };
        },
        sortingFn: currencySortingFn,
        cell: (info) => {
          const invoices = info.row.original.open_invoices || [];
          if (invoices.length === 0) {
            return <i className="text-muted">No open invoices</i>;
          }
          const val = info.getValue();
          const amount = val.amount;
          return (
            <span>
              {formatCurrency(val)}
              {invoices.length > 1 && (
                <div style={{ fontSize: '0.8rem', color: 'red' }}>
                  ({invoices.length} invoices open!)
                </div>
              )}
            </span>
          );
        },
      },
      {
        accessorKey: 'date_order',
        header: 'Order date',
        cell: (info) => info.getValue() ? formatDate(new Date(info.getValue()), 'P') : '-',
      },
      {
        accessorKey: 'next_action',
        header: 'Next action',
        cell: (info) => {
          const val = info.getValue();
          if (!val) return '-';
          const date = new Date(val);
          let color = 'inherit';
          let fontWeight = 'normal';

          if (isToday(date)) {
            color = 'red';
            fontWeight = 'bold';
          } else if (isTomorrow(date)) {
            color = 'orange';
          } else if (isBefore(date, startOfDay(new Date()))) {
            color = 'maroon';
          }

          return (
            <span style={{ color, fontWeight }}>
              {formatDate(date, 'P')}
            </span>
          );
        },
      },
      {
        accessorKey: 'eta',
        header: 'ETA',
        cell: (info) => info.getValue() ? formatDate(new Date(info.getValue()), 'P') : '-',
      },
      {
        accessorKey: 'last_modified_user_id',
        header: 'Last modified by',
      },
      {
        accessorKey: 'last_modified_date',
        header: 'Last modified',
        cell: (info) => info.getValue() ? formatDate(new Date(info.getValue()), 'P HH:mm') : '-',
      },
    ],
    [mode]
  );

  const expandedContent = (data) => (
    <>
      {data.internal_info && data.internal_info !== 'False' && (
        <div className="mb-3 border rounded p-3 bg-white shadow-sm">
          <strong>Internal Info:</strong>
          <div style={{ whiteSpace: 'pre-wrap' }}>{data.internal_info}</div>
        </div>
      )}
      {data.pickings?.length > 0 ? (
        <div>
          <h6 className="mb-2">Pickings and Products</h6>
          {data.pickings.map((picking, pIdx) => (
            <div key={picking.id || pIdx} className="mb-3 border rounded p-3 bg-white shadow-sm">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="fw-bold"><a href={picking.url} target="_blank">{picking.name}</a></span>
                <span className="badge bg-info">{picking.state}</span>
              </div>
              {picking.internal_info && picking.internal_info !== 'False' && (
                <div className="mb-3 p-2 bg-light rounded" style={{ fontSize: '0.85rem' }}>
                  <strong>Picking Internal Info:</strong>
                  <div style={{ whiteSpace: 'pre-wrap' }}>{picking.internal_info}</div>
                </div>
              )}
              <Table size="sm" bordered hover className="mt-2 mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Product</th>
                    <th>SKU</th>
                    <th className="text-end">Quantity</th>
                  </tr>
                </thead>
                <tbody>
                  {picking.moves?.map((move) => (
                    <tr key={move.id}>
                      <td>{move.product_name}</td>
                      <td>{move.product_default_code}</td>
                      <td className="text-end">{move.product_uom_qty}</td>
                    </tr>
                  )) || (
                    <tr>
                      <td colSpan="3" className="text-center text-muted italic">No products in this picking</td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-muted">No pickings found for this row.</div>
      )}
    </>
  );

  const globalFilterFn = (row, columnId, filterValue) => {
    const search = filterValue.toLowerCase();
    const name = (row.original.name || '').toLowerCase();
    const partner = (row.original.partner_id || '').toLowerCase();
    if (name.includes(search) || partner.includes(search)) return true;

    if ((row.original.lines || []).some((line) =>
      (line.product?.name || '').toLowerCase().includes(search) ||
      (line.product?.default_code || '').toLowerCase().includes(search)
    )) return true;

    return (row.original.pickings || []).some((picking) => 
      (picking.moves || []).some((move) => 
        (move.product_name || '').toLowerCase().includes(search) ||
        (move.product_default_code || '').toLowerCase().includes(search)
      )
    );
  };

  const getIsDone = useCallback((data) => {
    if (!data.pickings || data.pickings.length === 0) return false;
    return data.pickings.every(p => p.state === 'done' || p.state === 'cancel');
  }, []);

  return (
    <IntransitBasePage 
      mode={mode} 
      label={label} 
      columns={columns} 
      expandedContent={expandedContent}
      globalFilterFn={globalFilterFn}
      searchHelp={`Search by ${mode === 'purchases' ? 'PO' : 'SO'}, Company, or Product (Name/SKU)`}
      getIsDone={getIsDone}
    />
  );
}
